import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useBreakpoint, BreakpointKey } from '../hooks/useBreakpoint';
import { useLocation } from 'react-router-dom';

export interface DocumentationSection {
  id: string;
  title: string;
  slug: string;
  category: string;
  description?: string;
  path: string;
}

export interface NavigationState {
  // Mobile menu state
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  
  // Documentation mega menu state
  docsMenuOpen: boolean;
  setDocsMenuOpen: (open: boolean) => void;
  
  // Documentation data
  documentationSections: DocumentationSection[];
  setDocumentationSections: (sections: DocumentationSection[]) => void;
  
  // Current page and section tracking
  currentPage: string;
  currentSection?: string;
  currentCategory?: string;
  
  // Responsive state
  currentBreakpoint: BreakpointKey;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // Scroll state
  isScrolled: boolean;
  
  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: DocumentationSection[];
  setSearchResults: (results: DocumentationSection[]) => void;
  
  // Actions
  navigateToSection: (section: string) => void;
  closeMobileMenu: () => void;
  closeDocsMenu: () => void;
  toggleDocumentationMenu: () => void;
  closeAllMenus: () => void;
}

const NavigationContext = createContext<NavigationState | undefined>(undefined);

export const useNavigation = (): NavigationState => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  const breakpoint = useBreakpoint();
  
  // State management
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [docsMenuOpen, setDocsMenuOpen] = useState(false);
  const [documentationSections, setDocumentationSections] = useState<DocumentationSection[]>([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentationSection[]>([]);
  
  // Derived state
  const currentPage = location.pathname;
  const currentSection = location.hash ? location.hash.substring(1) : undefined;
  
  // Extract category from documentation path
  const currentCategory = currentPage.startsWith('/docs') 
    ? currentPage.split('/')[2] 
    : undefined;

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when breakpoint changes to desktop
  useEffect(() => {
    if (breakpoint.isDesktop && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [breakpoint.isDesktop, mobileMenuOpen]);

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setDocsMenuOpen(false);
  }, [location.pathname]);

  // Handle escape key to close menus
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeAllMenus();
      }
    };

    if (mobileMenuOpen || docsMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [mobileMenuOpen, docsMenuOpen]);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen && breakpoint.isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen, breakpoint.isMobile]);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = documentationSections.filter(section =>
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, documentationSections]);

  // Action handlers
  const navigateToSection = (section: string) => {
    // This will be implemented when integrating with router
    window.location.hash = section;
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const closeDocsMenu = () => {
    setDocsMenuOpen(false);
  };

  const toggleDocumentationMenu = () => {
    setDocsMenuOpen(prev => !prev);
  };

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setDocsMenuOpen(false);
  };

  const contextValue: NavigationState = {
    // Mobile menu state
    mobileMenuOpen,
    setMobileMenuOpen,
    
    // Documentation mega menu state
    docsMenuOpen,
    setDocsMenuOpen,
    
    // Documentation data
    documentationSections,
    setDocumentationSections,
    
    // Current page and section tracking
    currentPage,
    currentSection,
    currentCategory,
    
    // Responsive state
    currentBreakpoint: breakpoint.current,
    isMobile: breakpoint.isMobile,
    isTablet: breakpoint.isTablet,
    isDesktop: breakpoint.isDesktop,
    
    // Scroll state
    isScrolled,
    
    // Search state
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    
    // Actions
    navigateToSection,
    closeMobileMenu,
    closeDocsMenu,
    toggleDocumentationMenu,
    closeAllMenus
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};